import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DropTrailerFlagConfig } from './drop-trailer-flags-cell-renderer.component';

export interface DropTrailerFlowManagerDialogData {
  row: Record<string, any>;
  flags: DropTrailerFlagConfig[];
}

export interface DropTrailerFlowManagerItem {
  field: string;
  label: string;
  shortLabel: string;
  complete: boolean;
  date: string;
  time: string;
}

export interface DropTrailerFlowManagerDialogResult {
  items: DropTrailerFlowManagerItem[];
}

interface EditableFlowItem extends DropTrailerFlowManagerItem {
  index: number;
}

@Component({
  selector: 'app-drop-trailer-flow-manager-dialog',
  template: `
    <div class="w-[620px] max-w-[94vw] overflow-hidden rounded-2xl bg-white text-slate-950 shadow-2xl">
      <div class="border-b border-slate-200 bg-slate-50 px-6 py-5">
        <p class="m-0 text-xs font-black uppercase tracking-[0.14em] text-sky-600">Trailer Flow</p>
        <div class="mt-1 flex items-start justify-between gap-4">
          <div class="min-w-0">
            <h2 class="m-0 text-xl font-black leading-7 text-slate-950">Manage Milestones</h2>
            <p class="m-0 mt-1 truncate text-sm font-semibold text-slate-500">{{ orderLabel }}</p>
          </div>
          <span class="inline-flex h-8 items-center rounded-full bg-slate-200 px-3 text-xs font-black text-slate-700">
            {{ completedCount }}/{{ items.length }}
          </span>
        </div>
      </div>

      <div class="max-h-[62vh] overflow-auto px-6 py-5">
        <div class="grid gap-2">
          <div
            *ngFor="let item of items; let index = index"
            class="grid grid-cols-[2rem_1fr_9.5rem_7.5rem] items-center gap-3 rounded-xl border px-3 py-2 transition"
            [ngClass]="item.complete ? 'border-emerald-200 bg-emerald-50/70' : 'border-slate-200 bg-white'"
          >
            <label class="flex items-center justify-center">
              <input
                type="checkbox"
                class="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                [ngModel]="item.complete"
                (ngModelChange)="setComplete(index, $event)"
              />
            </label>

            <div class="min-w-0">
              <div class="truncate text-sm font-black text-slate-900">{{ item.label }}</div>
              <div class="mt-0.5 text-xs font-bold text-slate-400">Step {{ item.index + 1 }}</div>
            </div>

            <input
              type="date"
              class="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-950 outline-none transition disabled:bg-slate-100 disabled:text-slate-400 focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              [(ngModel)]="item.date"
              [disabled]="!item.complete"
              (ngModelChange)="onDateTimeChanged(index)"
            />

            <input
              type="time"
              class="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-bold text-slate-950 outline-none transition disabled:bg-slate-100 disabled:text-slate-400 focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              [(ngModel)]="item.time"
              [disabled]="!item.complete"
              (ngModelChange)="onDateTimeChanged(index)"
            />
          </div>
        </div>

        <p class="m-0 mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold leading-5 text-slate-500">
          Completing a later milestone also completes earlier blank milestones. Clearing a milestone clears that milestone and every later one.
        </p>
      </div>

      <div class="flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-6 py-5">
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 px-4 text-sm font-black text-slate-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          (click)="onCancel()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl bg-sky-600 px-4 text-sm font-black text-white shadow-lg shadow-sky-950/20 transition hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
          [disabled]="!isValid"
          (click)="onSave()"
        >
          Save Flow
        </button>
      </div>
    </div>
  `,
})
export class DropTrailerFlowManagerDialogComponent {
  items: EditableFlowItem[] = [];
  orderLabel = '';

  constructor(
    private dialogRef: MatDialogRef<DropTrailerFlowManagerDialogComponent, DropTrailerFlowManagerDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: DropTrailerFlowManagerDialogData
  ) {
    this.orderLabel = this.resolveOrderLabel(data.row);
    this.items = (data.flags || []).map((flag, index) => this.buildItem(flag, index, data.row || {}));
  }

  get completedCount(): number {
    return this.items.filter((item) => item.complete).length;
  }

  get isValid(): boolean {
    return this.items.every((item) => !item.complete || (!!item.date && !!item.time));
  }

  setComplete(index: number, complete: boolean): void {
    const item = this.items[index];
    if (!item) return;

    if (complete) {
      const stamp = this.defaultStamp(item);
      for (let i = 0; i <= index; i++) {
        const candidate = this.items[i];
        candidate.complete = true;
        candidate.date = candidate.date || stamp.date;
        candidate.time = candidate.time || stamp.time;
      }
      return;
    }

    for (let i = index; i < this.items.length; i++) {
      this.items[i].complete = false;
      this.items[i].date = '';
      this.items[i].time = '';
    }
  }

  onDateTimeChanged(index: number): void {
    const item = this.items[index];
    if (!item || item.complete || !item.date || !item.time) return;
    this.setComplete(index, true);
  }

  onSave(): void {
    if (!this.isValid) return;
    this.dialogRef.close({
      items: this.items.map((item) => ({
        field: item.field,
        label: item.label,
        shortLabel: item.shortLabel,
        complete: item.complete,
        date: item.date,
        time: item.time,
      })),
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private buildItem(flag: DropTrailerFlagConfig, index: number, row: Record<string, any>): EditableFlowItem {
    const atField = `${flag.field}_at`;
    const date = this.normalizeDate(row[flag.dateField || '']) || this.normalizeDate(row[atField]);
    const time = this.normalizeTime(row[flag.timeField || '']) || this.normalizeTime(row[atField]);

    return {
      index,
      field: flag.field,
      label: flag.label,
      shortLabel: flag.shortLabel,
      complete: this.toBoolean(row[flag.field]),
      date,
      time,
    };
  }

  private defaultStamp(item: EditableFlowItem): { date: string; time: string } {
    return {
      date: item.date || this.today(),
      time: item.time || this.nowTime(),
    };
  }

  private resolveOrderLabel(row: Record<string, any>): string {
    return String(
      row?.['alternate_reference']
      || row?.['lading_number']
      || row?.['booking_number']
      || row?.['trip_number']
      || 'Drop trailer'
    );
  }

  private today(): string {
    const now = new Date();
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private nowTime(): string {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }

  private normalizeDate(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  private normalizeTime(value: any): string {
    if (value === null || value === undefined) return '';
    const match = String(value).trim().match(/(?:^|\s)(\d{2}:\d{2})/);
    return match ? match[1] : '';
  }

  private toBoolean(raw: any): boolean {
    if (typeof raw === 'string') {
      const value = raw.trim().toLowerCase();
      return value === 'true' || value === 't' || value === '1' || value === 'yes' || value === 'y';
    }

    return raw === true || raw === 1;
  }
}
